import React, { useState, useRef, useEffect, useCallback } from 'react';
import InputForm from './components/InputForm';
import ResultsDisplay from './components/ResultsDisplay';
import LoadingState from './components/LoadingState';
import ErrorState from './components/ErrorState';
import FooterLinks from './components/FooterLinks';
import SaveReportPanel from './components/SaveReportPanel';
import Logo from './components/Logo';
import CredibilityAudit from './components/CredibilityAudit';
import LockedPreview from './components/LockedPreview';import PaymentVerifying from './components/PaymentVerifying';
import FAQ from './components/FAQ';
import FreeReportDialog from './components/FreeReportDialog';
import { track, EVENTS } from './lib/analytics';
import { CHATGPT_URL } from './lib/chatgpt';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const TEASER_STORAGE_KEY = 'geobull:lastAnalysisId';
const OWNER_TOKEN_KEY = 'geobull:ownerToken';

// Module-level guard for the "See Sample Report" attention pulse.
// React 18 StrictMode mounts effects twice in dev; a component-local
// useRef would also reset on the second mount. A module-scoped flag is
// initialised exactly once per page load, so the pulse decision is
// stable across StrictMode re-renders. In production builds there is
// no StrictMode and this acts as a normal "ran once" sentinel.
let _pulseHandled = false;

// Helpers ---------------------------------------------------------------
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getOwnerToken = () => {
  try {
    return sessionStorage.getItem(OWNER_TOKEN_KEY) || null;
  } catch (_) {
    return null;
  }
};

const teaserToFakeResults = (teaser, full) => ({
  raw: full.raw,
  parsed: full.parsed,
  date: full.date || teaser?.date || '',
  sources: full.sources || {},
  success: true,
  error: null,
});

function App() {
  // Submission lifecycle
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState('');
  const [event, setEvent] = useState('');

  // Paywall state
  // teaser is shown post-analyze, pre-payment
  // results+audit are shown only after successful payment
  const [teaser, setTeaser] = useState(null);
  const [results, setResults] = useState(null);
  const [audit, setAudit] = useState(null);

  // Unlock CTA state (Stripe redirect lifecycle)
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState(null);

  // Post-Stripe verification state
  // 'idle' | 'verifying' | 'success' | 'failed'
  const [verifyState, setVerifyState] = useState('idle');
  const [verifyMessage, setVerifyMessage] = useState(null);
  const [pendingSessionId, setPendingSessionId] = useState(null);

  // Cancel-from-Stripe banner (3 states)
  // 'idle' = no cancel, 'preserved' = teaser rehydrated successfully,
  // 'lost' = teaser could not be restored (stale, expired, or no analysis_id)
  const [cancelState, setCancelState] = useState('idle');

  // Sample mode — read-only public sample report (?sample=1)
  const [sampleMode, setSampleMode] = useState(false);
  const [sampleLoading, setSampleLoading] = useState(false);

  // Free-report flow state.
  // `freeReportDialogOpen` — controls the email-entry dialog visibility.
  // `freeRunToken` — single-use token issued by the magic-link verify
  //                  endpoint. Stored in sessionStorage so a page refresh
  //                  doesn't strand the user. Consumed on the next analyze.
  // `freeUnlockBanner` — transient banner shown after magic-link click
  //                      ("You're verified — run one free report now.").
  const [freeReportDialogOpen, setFreeReportDialogOpen] = useState(false);
  const [freeRunToken, setFreeRunToken] = useState(() => {
    try { return sessionStorage.getItem('gb_free_run_token') || null; }
    catch { return null; }
  });
  const [freeUnlockBanner, setFreeUnlockBanner] = useState(null);

  // "See Sample Report" CTA pulse — plays once per browser session to draw
  // a new visitor's eye to the sample button without becoming annoying.
  // We gate on sessionStorage (not localStorage) so a power user can get
  // the pulse again in a fresh session, but the animation never repeats
  // during normal in-session navigation. The module-level _pulseHandled
  // guard (defined outside the component) makes this StrictMode-safe —
  // a second mount in dev cannot re-trigger or skip the pulse incorrectly.
  const [sampleCtaPulse, setSampleCtaPulse] = useState(false);
  useEffect(() => {
    if (_pulseHandled) return;
    _pulseHandled = true;
    try {
      // Skip the pulse entirely on direct sample/cancel/payment-return loads
      // since the homepage CTA won't even be shown.
      const params = new URLSearchParams(window.location.search);
      if (
        params.has('sample') ||
        params.has('session_id') ||
        params.has('cancelled')
      ) return;
      const KEY = 'gb_sample_cta_pulsed';
      if (sessionStorage.getItem(KEY)) return;
      sessionStorage.setItem(KEY, '1');
      // NOTE: we deliberately do not return a cleanup function here.
      // StrictMode (dev only) runs cleanup between its synthetic
      // double-mount cycle. If we clearTimeout in cleanup, the 250ms
      // pulse-on timer never fires. Leaving the timers uncancelled is
      // safe — setState on an unmounted component is a silent no-op
      // in React 18, and these timers are short (max 3.5s).
      setTimeout(() => setSampleCtaPulse(true), 250);
      setTimeout(() => setSampleCtaPulse(false), 3500);
    } catch (_) {
      /* sessionStorage disabled — skip pulse silently */
    }
  }, []);

  // Owner-only "Promote to sample" state
  // 'idle' | 'promoting' | 'success' | 'error'
  const [promoteState, setPromoteState] = useState('idle');
  const [promoteMessage, setPromoteMessage] = useState(null);

  const handlePromoteToSample = useCallback(async () => {
    if (!teaser?.analysis_id) return;
    const token = getOwnerToken();
    if (!token) return;
    setPromoteState('promoting');
    setPromoteMessage(null);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/owner/promote-sample/${encodeURIComponent(teaser.analysis_id)}`,
        {
          method: 'POST',
          headers: { 'X-Owner-Token': token },
        }
      );
      if (!r.ok) {
        let detail = `HTTP ${r.status}`;
        try { const j = await r.json(); detail = j?.detail || detail; } catch (_) { /* ignore */ }
        throw new Error(detail);
      }
      setPromoteState('success');
      setPromoteMessage('This report is now the public sample on /?sample=1.');
    } catch (e) {
      setPromoteState('error');
      setPromoteMessage(e.message || 'Could not promote — please try again.');
    }
  }, [teaser]);

  const resultsRef = useRef();

  // ---------------------------------------------------------------------
  // Network resilience helpers
  // ---------------------------------------------------------------------
  // A small wrapper around fetch() that retries on TRANSIENT network errors
  // (the kind that surface as "Failed to fetch" in Chrome / "Load failed"
  // in Safari). We do NOT retry on 4xx / 5xx HTTP responses — those are
  // structured errors the caller wants to see. We only retry when fetch()
  // itself rejected, which means TCP/TLS/DNS/CORS-level transient flakiness.
  //
  // Used for: the initial /api/analyze POST, the owner unlock POST, and
  // (inside try/catch) the status poll loop. Without this, a single mid-
  // request network blip on a phone walking through a building kills the
  // entire owner-mode analysis with a cryptic "Failed to fetch".
  const fetchWithRetry = async (url, options = {}, { attempts = 3, backoffMs = 800 } = {}) => {
    let lastErr = null;
    for (let i = 0; i < attempts; i++) {
      try {
        return await fetch(url, options);
      } catch (e) {
        lastErr = e;
        // eslint-disable-next-line no-console
        console.warn(`fetchWithRetry: attempt ${i + 1}/${attempts} failed for ${url}:`, e?.message || e);
        if (i < attempts - 1) {
          await new Promise((res) => setTimeout(res, backoffMs * (i + 1)));
        }
      }
    }
    throw lastErr || new Error('Network error');
  };

  // Detect "we couldn't reach the server" failures and surface a
  // user-friendly message instead of the raw browser string.
  const friendlyNetworkError = (err) => {
    const msg = String(err?.message || err || '').toLowerCase();
    if (
      msg.includes('failed to fetch') ||
      msg.includes('load failed') ||
      msg.includes('networkerror') ||
      msg.includes('network error') ||
      msg.includes('the network connection was lost')
    ) {
      return 'Network connection interrupted. Please check your signal and try again.';
    }
    return null;
  };

  // ---------------------------------------------------------------------
  // ANALYZE — async job pattern: kick off, then poll status until ready/failed
  // ---------------------------------------------------------------------
  const handleAnalyze = async (companyInput, eventInput) => {
    setLoading(true);
    setError(null);
    setTeaser(null);
    setResults(null);
    setAudit(null);
    setUnlockError(null);
    setCancelState('idle');
    setCompany(companyInput);
    setEvent(eventInput);

    try {
      // 1) Kick off the job — returns immediately with {analysis_id, status:'processing'}
      track(EVENTS.ANALYSIS_STARTED, { company: companyInput, event: eventInput });
      const startResp = await fetchWithRetry(`${BACKEND_URL}/api/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company: companyInput, event: eventInput }),
      });

      // Read body as text first, then JSON.parse — avoids Safari's
      // "Body is disturbed or locked" error and lets us show the raw body
      // when the server returns non-JSON (e.g. an HTML 502 page).
      const startBodyText = await startResp.text();
      let startData = null;
      try {
        startData = startBodyText ? JSON.parse(startBodyText) : null;
      } catch (_) {
        startData = null;
      }

      if (!startResp.ok || !startData?.analysis_id) {
        // Pydantic 422 returns detail as an array of {loc, msg, type, ...}
        // — turn it into a single readable sentence.
        let msg = null;
        const d = startData?.detail;
        if (Array.isArray(d) && d.length > 0) {
          msg = d
            .map((e) => {
              const field = Array.isArray(e?.loc) ? e.loc.slice(-1)[0] : null;
              return field ? `${field}: ${e.msg}` : e.msg;
            })
            .filter(Boolean)
            .join('; ');
        } else if (typeof d === 'string') {
          msg = d;
        } else if (startData?.error) {
          msg = startData.error;
        }
        if (!msg) {
          msg = startBodyText
            ? `Server error ${startResp.status}: ${startBodyText.slice(0, 160)}`
            : `Server error ${startResp.status}`;
        }
        throw new Error(msg);
      }

      // 2) Poll /api/analyze/status/{id} until ready or failed.
      // Each tick is wrapped in try/catch so a transient network blip
      // (phone going behind a wall, brief CDN edge hiccup, etc.) does
      // not kill the entire analysis run — we just skip that tick and
      // poll again. Only if we exhaust nearly all attempts do we surface
      // the error.
      const analysisId = startData.analysis_id;
      const maxAttempts = 100;      // 100 × 2.5s = 250s ceiling
      const intervalMs = 2500;
      let teaserData = null;
      let consecutiveNetworkFails = 0;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await sleep(intervalMs);
        let r;
        try {
          r = await fetch(
            `${BACKEND_URL}/api/analyze/status/${encodeURIComponent(analysisId)}`
          );
          consecutiveNetworkFails = 0;
        } catch (netErr) {
          consecutiveNetworkFails += 1;
          // eslint-disable-next-line no-console
          console.warn(`Poll network error (${consecutiveNetworkFails}/5):`, netErr?.message || netErr);
          // Tolerate up to 5 consecutive network blips (~12s of dead air).
          if (consecutiveNetworkFails >= 5) {
            throw new Error('Network connection interrupted. Please check your signal and try again.');
          }
          continue;
        }
        if (!r.ok) {
          // Transient HTTP error — keep polling unless we've nearly run out
          if (attempt > maxAttempts - 4) {
            throw new Error(`Status check failed (${r.status})`);
          }
          continue;
        }
        const sData = await r.json();
        if (sData.status === 'ready' && sData.teaser) {
          teaserData = sData.teaser;
          break;
        }
        if (sData.status === 'failed') {
          throw new Error(sData.error || 'Analysis failed');
        }
        // status === 'processing' — keep polling
      }

      if (!teaserData) {
        throw new Error('Analysis timed out — please try again.');
      }

      setTeaser(teaserData);
      track(EVENTS.ANALYSIS_READY, {
        analysis_id: teaserData.analysis_id,
        ticker: teaserData.ticker,
        event: teaserData.event,
      });
      track(EVENTS.PAYWALL_SHOWN, { analysis_id: teaserData.analysis_id });
      try {
        localStorage.setItem(TEASER_STORAGE_KEY, teaserData.analysis_id);
      } catch (_) {
        /* ignore storage errors */
      }

      // Owner bypass — if a valid owner token is in sessionStorage, immediately
      // unlock the analysis and skip the Stripe paywall entirely.
      // Uses fetchWithRetry so a transient network blip on the unlock call
      // doesn't surface as "Failed to fetch" after a successful analysis.
      const ownerToken = getOwnerToken();
      if (ownerToken) {
        try {
          const r = await fetchWithRetry(
            `${BACKEND_URL}/api/owner/unlock/${encodeURIComponent(teaserData.analysis_id)}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Owner-Token': ownerToken,
              },
            }
          );
          if (r.ok) {
            const full = await r.json();
            setResults(teaserToFakeResults(teaserData, full));
            setAudit(full.audit || null);
            setTeaser((prev) => prev ? { ...prev, paid: true } : prev);
            setUnlockError(null);
            track(EVENTS.OWNER_BYPASS_USED, {
              analysis_id: teaserData.analysis_id,
              ticker: teaserData.ticker,
            });
          } else {
            // Show a clear error so the user knows owner mode failed (and why)
            let detail = '';
            try {
              const j = await r.json();
              detail = j?.detail || JSON.stringify(j);
            } catch (_) { detail = `HTTP ${r.status}`; }
            const msg = `Owner mode rejected by server (${r.status}): ${detail}. Falling back to paywall. To re-try, click "Exit owner mode" then re-open your bookmarked URL.`;
            console.warn(msg);
            setUnlockError(msg);
            // Only purge the token on a hard auth failure (403). For 404/409
            // (analysis not ready / not found) we keep the token so subsequent
            // analyses can still try owner mode.
            if (r.status === 403) {
              try { sessionStorage.removeItem(OWNER_TOKEN_KEY); } catch (_) { /* ignore */ }
            }
          }
        } catch (e) {
          const msg = `Owner unlock network error: ${e.message || e}. Falling back to paywall.`;
          console.warn(msg, e);
          setUnlockError(msg);
        }
      }

      // Free-run token unlock — same pattern as owner unlock. If a single-
      // use token issued via the magic-link flow is present, redeem it now
      // to unlock this analysis without going through Stripe. The token is
      // consumed server-side and removed from sessionStorage either way
      // (a 403 means it was already used / invalid; a 200 means it was
      // accepted and is now spent).
      else if (freeRunToken) {
        try {
          const r = await fetchWithRetry(
            `${BACKEND_URL}/api/free-report/unlock/${encodeURIComponent(teaserData.analysis_id)}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Free-Token': freeRunToken,
              },
            }
          );
          if (r.ok) {
            const full = await r.json();
            setResults(teaserToFakeResults(teaserData, full));
            setAudit(full.audit || null);
            setTeaser((prev) => prev ? { ...prev, paid: true } : prev);
            setFreeUnlockBanner(null);
            track(EVENTS.FREE_REPORT_REDEEMED || 'free_report_redeemed', {
              analysis_id: teaserData.analysis_id,
              ticker: teaserData.ticker,
            });
          } else {
            let detail = '';
            try {
              const j = await r.json();
              detail = j?.detail || JSON.stringify(j);
            } catch (_) { detail = `HTTP ${r.status}`; }
            console.warn(`Free-run unlock failed (${r.status}): ${detail}`);
            // Surface a non-fatal banner; the user can still pay $1 to proceed.
            setFreeUnlockBanner({
              kind: 'error',
              text: `Your free-report token couldn't be redeemed (${detail}). Pay $1 to unlock this report.`,
            });
          }
        } catch (e) {
          console.warn('Free-run unlock network error:', e?.message || e);
          setFreeUnlockBanner({
            kind: 'error',
            text: 'Network error while redeeming your free token. Pay $1 to unlock this report.',
          });
        } finally {
          // Token is single-use — clear it regardless of outcome so the
          // next analyze goes through the normal paywall.
          try { sessionStorage.removeItem('gb_free_run_token'); } catch (_) { /* ignore */ }
          setFreeRunToken(null);
        }
      }
    } catch (err) {
      console.error('Analysis error:', err);
      // Translate raw browser network strings into a user-readable message.
      const friendly = friendlyNetworkError(err);
      setError(friendly || err.message || 'Analysis failed. Please try again.');
      track(EVENTS.ANALYSIS_FAILED, { message: String(err?.message || err) });
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------
  // UNLOCK — create checkout session and redirect
  // ---------------------------------------------------------------------
  const handleUnlock = async () => {
    if (!teaser?.analysis_id) return;
    setUnlocking(true);
    setUnlockError(null);
    setCancelState('idle');
    track(EVENTS.CHECKOUT_STARTED, {
      analysis_id: teaser.analysis_id,
      ticker: teaser.ticker,
    });

    try {
      const response = await fetch(`${BACKEND_URL}/api/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysis_id: teaser.analysis_id,
          origin_url: window.location.origin,
        }),
      });
      const data = await response.json();

      if (!response.ok || !data?.url) {
        throw new Error(data?.detail || data?.error || 'Failed to start checkout');
      }

      // Persist analysis_id so we can re-hydrate the teaser on cancel-return
      try {
        localStorage.setItem(TEASER_STORAGE_KEY, teaser.analysis_id);
      } catch (_) { /* ignore */ }

      window.location.href = data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      setUnlockError(err.message || 'Could not start checkout — please try again.');
      setUnlocking(false);
    }
  };

  // ---------------------------------------------------------------------
  // POLL — verify payment status after Stripe return
  // ---------------------------------------------------------------------
  const pollPaymentStatus = useCallback(async (sessionId) => {
    const maxAttempts = 10;
    const intervalMs = 2500;

    // Read body as text first then JSON.parse — avoids Safari's
    // "Body is disturbed or locked" error when the response is unusual.
    const safeJson = async (resp) => {
      try {
        const txt = await resp.text();
        return txt ? JSON.parse(txt) : null;
      } catch (_) {
        return null;
      }
    };

    let lastErr = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const r = await fetch(
          `${BACKEND_URL}/api/checkout/status/${encodeURIComponent(sessionId)}`,
          { cache: 'no-store' }
        );
        const data = await safeJson(r);

        if (!r.ok) {
          // Backend returned non-2xx — keep polling unless we've nearly run out
          lastErr = new Error(
            data?.detail || data?.error || `Status check failed (${r.status})`
          );
          await sleep(intervalMs);
          continue;
        }

        if (data && data.payment_status === 'paid' && data.full) {
          setVerifyState('success');
          track(EVENTS.PAYMENT_COMPLETED, {
            session_id: sessionId,
            analysis_id: data.analysis_id,
            amount_usd: Number(data.amount_total) || 1,
            currency: data.currency || 'usd',
            $revenue: Number(data.amount_total) || 1,
          });
          // Briefly show success state, then transition to full results
          await sleep(450);
          setResults(teaserToFakeResults(teaser, data.full));
          setAudit(data.full.audit || null);
          setTeaser((prev) =>
            prev
              ? { ...prev, paid: true, ticker: data.full.parsed?.ticker || prev.ticker }
              : prev
          );
          window.history.replaceState({}, document.title, window.location.pathname);
          setVerifyState('idle');
          setPendingSessionId(null);
          return;
        }

        if (data && data.status === 'expired') {
          setVerifyState('failed');
          setVerifyMessage('Your Stripe checkout session expired. Please try again.');
          return;
        }

        // Still pending — wait and retry
        await sleep(intervalMs);
      } catch (err) {
        console.error('Status poll error:', err);
        lastErr = err;
        await sleep(intervalMs);
      }
    }

    // Exhausted retries
    setVerifyState('failed');
    setVerifyMessage(
      (lastErr && lastErr.message)
        ? `We could not confirm your payment yet (${lastErr.message}). If you paid, click "Check again" — or refresh and your unlock will appear shortly.`
        : 'Payment status is still pending. If you completed payment, click "Check again".'
    );
  }, [teaser]);

  const retryPoll = useCallback(() => {
    if (!pendingSessionId) return;
    setVerifyState('verifying');
    setVerifyMessage(null);
    pollPaymentStatus(pendingSessionId);
  }, [pendingSessionId, pollPaymentStatus]);

  // Sample-report loader with retry/backoff. Called on mount when ?sample=1
  // and also when the user clicks "Retry" after a transient failure.
  // Mobile Safari occasionally drops the first XHR after a cold tab open;
  // a short retry loop converts that single transient failure into success
  // without the user ever seeing an error banner.
  const loadSampleReport = useCallback(async () => {
    setError(null);
    setSampleLoading(true);
    const maxAttempts = 3;
    let lastErr = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 15000);
        const r = await fetch(`${BACKEND_URL}/api/sample`, {
          cache: 'no-store',
          signal: ctrl.signal,
        });
        clearTimeout(t);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!data || !data.teaser || !data.full || !data.full.raw) {
          throw new Error('Malformed sample payload');
        }
        const fakeResults = teaserToFakeResults(data.teaser, data.full);
        setResults(fakeResults);
        setAudit(data.full?.audit || null);
        setCompany(data.company_input || data.teaser?.company_name || '');
        setEvent(data.event || data.teaser?.event || '');
        setTeaser({ ...data.teaser, paid: true });
        setSampleLoading(false);
        return;
      } catch (e) {
        lastErr = e;
        // eslint-disable-next-line no-console
        console.warn(`Sample load attempt ${attempt}/${maxAttempts} failed:`, e?.message || e);
        if (attempt < maxAttempts) {
          // Exponential-ish backoff: 800ms, 1600ms
          await new Promise((res) => setTimeout(res, 800 * attempt));
        }
      }
    }
    // eslint-disable-next-line no-console
    console.error('Sample load failed after retries:', lastErr);
    setError('The sample report is temporarily unavailable. Please try again shortly.');
    setSampleLoading(false);
  }, []);

  // ---------------------------------------------------------------------
  // ON MOUNT — detect ?owner=, Stripe return (success or cancel), and rehydrate
  // ---------------------------------------------------------------------
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const cancelled = params.get('cancelled');
    const urlAnalysisId = params.get('analysis_id');
    const ownerParam = params.get('owner');
    const sampleParam = params.get('sample');
    const freeParam = params.get('free');

    // ?free=<request_id> → return from a magic-link email click. Verify
    // the token server-side, then either:
    //   • on success: store the issued free_run_token, strip the URL,
    //     and show a banner reminding the user to run their free analysis
    //   • on failure: surface the error and offer to request a new link
    if (freeParam) {
      (async () => {
        try {
          const r = await fetch(`${BACKEND_URL}/api/free-report/verify/${encodeURIComponent(freeParam)}`);
          const data = await r.json();
          if (data.status === 'verified' && data.free_run_token) {
            try { sessionStorage.setItem('gb_free_run_token', data.free_run_token); } catch { /* ignore */ }
            setFreeRunToken(data.free_run_token);
            setFreeUnlockBanner({
              kind: 'success',
              text: 'You\'re verified — run one free analysis now. The token is single-use.',
            });
            track(EVENTS.FREE_REPORT_VERIFIED || 'free_report_verified', {});
          } else {
            setFreeUnlockBanner({
              kind: 'error',
              text: data.message || 'This link is no longer valid. Please request a new one.',
            });
          }
        } catch (e) {
          setFreeUnlockBanner({
            kind: 'error',
            text: 'Could not verify the link. Please request a new one.',
          });
        } finally {
          // Strip ?free out of the URL so the verify isn't re-attempted on
          // refresh and the token doesn't show up in shared screenshots.
          const cleanParams = new URLSearchParams(window.location.search);
          cleanParams.delete('free');
          const ns = cleanParams.toString();
          window.history.replaceState(
            {}, document.title,
            window.location.pathname + (ns ? '?' + ns : '')
          );
        }
      })();
      return;  // don't also process other URL params on this load
    }

    // ?owner=<secret> → save to sessionStorage so all subsequent API calls
    // can carry the X-Owner-Token header. Token persists for the tab lifetime.
    if (ownerParam) {
      try { sessionStorage.setItem(OWNER_TOKEN_KEY, ownerParam.trim()); } catch (_) { /* ignore */ }
      track(EVENTS.OWNER_MODE_ACTIVATED, {});
      // Strip the secret out of the URL so it isn't leaked in screenshots / history
      const cleanParams = new URLSearchParams(window.location.search);
      cleanParams.delete('owner');
      const newSearch = cleanParams.toString();
      window.history.replaceState(
        {},
        document.title,
        window.location.pathname + (newSearch ? '?' + newSearch : '')
      );
    }

    // ?sample=1 → load the public read-only sample report. Skips paywall,
    // skips analysis flow entirely. The Stripe / cancel return paths below
    // are mutually exclusive with sample mode, so we short-circuit early.
    if (sampleParam === '1' || sampleParam === 'true') {
      setSampleMode(true);
      track(EVENTS.SAMPLE_REPORT_VIEWED, {});
      loadSampleReport();
      return; // do not also process Stripe-return params in sample mode
    }

    const rehydrateTeaser = async (analysisId) => {
      try {
        const r = await fetch(
          `${BACKEND_URL}/api/analysis/${encodeURIComponent(analysisId)}/teaser`
        );
        if (!r.ok) return null;
        const data = await r.json();
        setTeaser(data);
        return data;
      } catch (e) {
        console.warn('Teaser rehydrate failed:', e);
        return null;
      }
    };

    const handleReturn = async () => {
      if (sessionId) {
        // Returning from successful Stripe checkout
        const aid = urlAnalysisId || (() => {
          try { return localStorage.getItem(TEASER_STORAGE_KEY); } catch (_) { return null; }
        })();
        if (aid) {
          await rehydrateTeaser(aid);
        }
        setPendingSessionId(sessionId);
        setVerifyState('verifying');
        setVerifyMessage(null);
        pollPaymentStatus(sessionId);
        return;
      }

      if (cancelled === 'true') {
        const aid = urlAnalysisId || (() => {
          try { return localStorage.getItem(TEASER_STORAGE_KEY); } catch (_) { return null; }
        })();
        track(EVENTS.CHECKOUT_CANCELLED, { analysis_id: aid || null });
        // Try to rehydrate teaser; if successful, banner shows "preserved";
        // if not, show "lost" banner with a clear new-analysis path forward.
        let restored = null;
        if (aid) {
          restored = await rehydrateTeaser(aid);
        }
        setCancelState(restored ? 'preserved' : 'lost');
        if (!restored) {
          // Clean stale localStorage so the next cycle doesn't re-attempt
          try { localStorage.removeItem(TEASER_STORAGE_KEY); } catch (_) { /* ignore */ }
        }
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
    };

    handleReturn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--gb-offwhite))' }}>
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="mb-12 text-center">
          <Logo size="lg" />
          <p
            className="text-sm sm:text-base mt-3 max-w-3xl mx-auto leading-relaxed"
            style={{ color: 'hsl(var(--gb-text-muted))' }}
          >
            <span className="font-semibold" style={{ color: 'hsl(var(--gb-gold))' }}>$1</span>
            <span> introductory pricing. </span>
            Pick a US listed stock. Pick a geopolitical trigger. In about 90 seconds, our AI-enabled analytical model generates a report with five bull arguments and five bear arguments.{' '}
            <span className="font-semibold" style={{ color: 'hsl(var(--gb-navy))' }}>
              Every report is self-audited:
            </span>{' '}
            each argument is scored and the more credible side is named. Output is yours to use.
          </p>

          {/* "See Sample Report" CTA — promoted to a real button so it reads as
              a primary action, not buried inline. Pulses gently for ~3 seconds
              on first homepage open (per session) to draw the eye, then sits
              quietly. Hidden in sample mode (where it would be redundant) and
              once a report is in flight.
              Sits in a row with the "Get one free report" CTA on desktop;
              stacks on mobile. */}
          {!sampleMode && !teaser && !results && (
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <a
                href="?sample=1"
                data-testid="header-sample-link"
                className={`gb-sample-cta inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold uppercase tracking-[0.12em] transition-colors duration-150 ${sampleCtaPulse ? 'gb-pulse-attention' : ''}`}
                style={{
                  backgroundColor: 'hsl(var(--gb-navy))',
                  color: 'hsl(var(--gb-primary-foreground))',
                  border: '1px solid hsl(var(--gb-gold))',
                }}
                aria-label="See sample report"
              >
                See Sample Report
                <span aria-hidden="true" style={{ color: 'hsl(var(--gb-gold))' }}>→</span>
              </a>
              <button
                type="button"
                onClick={() => {
                  setFreeReportDialogOpen(true);
                  track(EVENTS.FREE_REPORT_DIALOG_OPENED || 'free_report_dialog_opened', {});
                }}
                data-testid="header-free-report-btn"
                className="gb-sample-cta inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold uppercase tracking-[0.12em] transition-colors duration-150"
                style={{
                  backgroundColor: 'transparent',
                  color: 'hsl(var(--gb-navy))',
                  border: '1px solid hsl(var(--gb-navy))',
                }}
                aria-label="Get a free report by email"
              >
                Get a Free Report
                <span aria-hidden="true" style={{ color: 'hsl(var(--gb-gold))' }}>✉</span>
              </button>
            </div>
          )}

          {/* Soft secondary CTA — "Ask ChatGPT what we do".
              Intentionally a low-visual-weight text link rather than a
              third button so it doesn't compete with See Sample Report /
              Get a Free Report as the primary actions. Opens a pre-
              filled ChatGPT chat in a new tab. Same prompt as the footer
              link (single source of truth in lib/chatgpt.js). */}
          {!sampleMode && !teaser && !results && (
            <p
              className="mt-4 text-xs sm:text-sm"
              style={{ color: 'hsl(var(--gb-text-muted))' }}
            >
              <span>Curious about us? </span>
              <a
                href={CHATGPT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-chatgpt-link"
                onClick={() => track('hero_chatgpt_click', {})}
                className="underline underline-offset-2 hover:opacity-80 transition-opacity"
                style={{ color: 'hsl(var(--gb-navy))', fontWeight: 600 }}
                title="Opens a pre-filled question about GeoBull / GeoBear in ChatGPT"
              >
                Ask ChatGPT what we do
              </a>
              <span aria-hidden="true" style={{ color: 'hsl(var(--gb-gold))' }}> →</span>
            </p>
          )}

          {/* Magic-link verify result banner (from ?free=<id> handler).
              Shown on first paint after the user clicks the email link. */}
          {freeUnlockBanner && (
            <div
              className="mt-4 rounded-md px-4 py-3 text-sm text-center"
              data-testid="free-unlock-banner"
              style={
                freeUnlockBanner.kind === 'success'
                  ? {
                      backgroundColor: 'hsl(var(--gb-gold) / 0.15)',
                      color: 'hsl(var(--gb-navy))',
                      border: '1px solid hsl(var(--gb-gold))',
                    }
                  : {
                      backgroundColor: 'hsl(var(--gb-rust) / 0.08)',
                      color: 'hsl(var(--gb-rust))',
                      border: '1px solid hsl(var(--gb-rust) / 0.4)',
                    }
              }
            >
              {freeUnlockBanner.text}
            </div>
          )}
        </header>

        {/* Sample-mode banner — read-only public sample */}
        {sampleMode && (
          <div
            className="mb-6 rounded-md border-2 px-4 py-3 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            style={{
              borderColor: 'hsl(var(--gb-navy))',
              backgroundColor: 'hsl(var(--gb-surface-2))',
              color: 'hsl(var(--gb-navy))',
            }}
            data-testid="sample-banner"
          >
            <span>
              <strong>Sample report.</strong> Generate your own custom analysis on any US stock
              and geopolitical trigger.
            </span>
            <a
              href="/"
              className="inline-flex items-center justify-center rounded-md border-2 px-3 py-1.5 text-xs font-semibold whitespace-nowrap hover:opacity-90 transition-opacity self-start sm:self-auto"
              style={{
                borderColor: 'hsl(var(--gb-navy))',
                backgroundColor: 'hsl(var(--gb-navy))',
                color: 'hsl(var(--gb-offwhite))',
              }}
              data-testid="sample-cta-generate"
            >
              Generate your own report →
            </a>
          </div>
        )}

        {/* Owner-mode indicator — visible only when a valid token is in sessionStorage */}
        {!sampleMode && getOwnerToken() && (
          <div
            className="mb-4 rounded-md border-2 px-4 py-2 text-xs flex items-center justify-between gap-3 max-w-md mx-auto"
            style={{
              borderColor: 'hsl(var(--gb-navy))',
              backgroundColor: 'hsl(var(--gb-surface-2))',
              color: 'hsl(var(--gb-navy))',
            }}
            data-testid="owner-mode-banner"
          >
            <span className="font-semibold">🔓 Owner mode — paywall bypassed</span>
            <button
              type="button"
              onClick={() => {
                try { sessionStorage.removeItem(OWNER_TOKEN_KEY); } catch (_) { /* ignore */ }
                window.location.reload();
              }}
              className="underline hover:no-underline"
              style={{ color: 'hsl(var(--gb-text-muted))' }}
              data-testid="owner-mode-exit"
            >
              Exit owner mode
            </button>
          </div>
        )}

        {/* Cancel notice — two flavors based on whether teaser was rehydrated */}
        {cancelState === 'preserved' && (
          <div
            className="mb-6 rounded-md border px-4 py-3 text-sm"
            style={{
              borderColor: 'hsl(var(--gb-gold))',
              backgroundColor: 'hsl(var(--gb-surface-2))',
              color: 'hsl(var(--gb-navy))',
            }}
            data-testid="cancel-notice"
          >
            Payment was cancelled — your report is still here. You can unlock it whenever you're ready.
          </div>
        )}
        {cancelState === 'lost' && (
          <div
            className="mb-6 rounded-md border-2 px-4 py-3 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
            style={{
              borderColor: 'hsl(var(--gb-gold))',
              backgroundColor: 'hsl(var(--gb-surface-2))',
              color: 'hsl(var(--gb-navy))',
            }}
            data-testid="cancel-notice-lost"
          >
            <span>
              <strong>Payment was cancelled.</strong> Your earlier report could not be restored — please run a new analysis below.
            </span>
            <button
              type="button"
              onClick={() => setCancelState('idle')}
              className="text-xs underline hover:no-underline self-start sm:self-auto"
              style={{ color: 'hsl(var(--gb-text-muted))' }}
              data-testid="dismiss-cancel-notice"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Input Form — hidden while a teaser/results is on screen so the user
            doesn't accidentally re-burn an analysis before paying for the current one.
            Also hidden in sample (read-only) mode. */}
        {!teaser && !results && !sampleMode && (
          <InputForm onAnalyze={handleAnalyze} disabled={loading} />
        )}

        {/* FAQ — only on the clean landing state. Hidden during loading, after
            a report is generated, in sample mode, or after an error so the user
            stays focused on the active flow. */}
        {!teaser && !results && !sampleMode && !loading && !error && !sampleLoading && (
          <FAQ />
        )}

        {/* Loading State (full analyze + audit is ~60s, or sample fetch) */}
        {(loading || sampleLoading) && <LoadingState />}

        {/* Error State (analyze failed) */}
        {error && (
          <ErrorState
            error={error}
            onRetry={() => {
              if (sampleMode) {
                loadSampleReport();
              } else {
                handleAnalyze(company, event);
              }
            }}
          />
        )}

        {/* Verifying Payment (after Stripe return) */}
        {verifyState !== 'idle' && (
          <PaymentVerifying
            state={verifyState}
            message={verifyMessage}
            onRetry={retryPoll}
          />
        )}

        {/* Locked Preview — analysis ran, awaiting payment */}
        {teaser && !results && verifyState === 'idle' && (
          <div className="mt-8">
            <LockedPreview
              teaser={teaser}
              onUnlock={handleUnlock}
              unlocking={unlocking}
              unlockError={unlockError}
            />
            {/* Allow user to start a new analysis if they want to abandon this teaser */}
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => {
                  setTeaser(null);
                  setUnlockError(null);
                  setCancelState('idle');
                  try { localStorage.removeItem(TEASER_STORAGE_KEY); } catch (_) { /* ignore */ }
                }}
                className="text-xs underline hover:no-underline"
                style={{ color: 'hsl(var(--gb-text-muted))' }}
                data-testid="discard-teaser-button"
              >
                Start over with a different ticker / event
              </button>
            </div>
          </div>
        )}

        {/* Full Results — only after payment confirmation */}
        {results && results.parsed && (
          <div className="mt-8 space-y-6">
            <SaveReportPanel
              results={results}
              company={company}
              audit={audit}
              testIdSuffix="top"
            />

            <ResultsDisplay
              ref={resultsRef}
              results={results}
              company={company}
            />

            <CredibilityAudit
              status={audit ? 'success' : 'error'}
              audit={audit}
              error={audit ? null : 'Audit not available'}
              onRetry={() => { /* no-op — audit already ran server-side */ }}
            />

            <SaveReportPanel
              results={results}
              company={company}
              audit={audit}
              testIdSuffix="bottom"
            />

            {/* Owner-only: Promote this report to be the public sample report.
                Hidden in sample mode and when no owner token is present. */}
            {!sampleMode && getOwnerToken() && teaser?.analysis_id && (
              <div
                className="rounded-md border-2 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                style={{
                  borderColor: 'hsl(var(--gb-navy))',
                  backgroundColor: 'hsl(var(--gb-surface-2))',
                  color: 'hsl(var(--gb-navy))',
                }}
                data-testid="promote-sample-panel"
              >
                <div className="text-sm">
                  <div className="font-semibold">Owner action</div>
                  <div className="text-xs" style={{ color: 'hsl(var(--gb-text-muted))' }}>
                    Make this report the public Sample Report shown at /?sample=1.
                  </div>
                  {promoteMessage && (
                    <div
                      className="mt-1 text-xs"
                      style={{
                        color: promoteState === 'success'
                          ? 'hsl(var(--gb-navy))'
                          : 'hsl(var(--gb-gold))',
                      }}
                      data-testid="promote-sample-message"
                    >
                      {promoteState === 'success' ? '✓ ' : '⚠ '}{promoteMessage}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handlePromoteToSample}
                  disabled={promoteState === 'promoting' || promoteState === 'success'}
                  className="inline-flex items-center justify-center rounded-md border-2 px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-opacity hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    borderColor: 'hsl(var(--gb-navy))',
                    backgroundColor: 'hsl(var(--gb-navy))',
                    color: 'hsl(var(--gb-offwhite))',
                  }}
                  data-testid="promote-sample-button"
                >
                  {promoteState === 'promoting'
                    ? 'Promoting…'
                    : promoteState === 'success'
                      ? '✓ Promoted'
                      : 'Promote to public sample'}
                </button>
              </div>
            )}

            {/* Reset / new run — or "Generate your own" CTA in sample mode */}
            <div className="text-center pt-2">
              {sampleMode ? (
                <a
                  href="/"
                  className="text-sm underline hover:no-underline"
                  style={{ color: 'hsl(var(--gb-navy))' }}
                  data-testid="sample-bottom-cta"
                >
                  ← Generate your own report on any US stock + geopolitical event
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setResults(null);
                    setAudit(null);
                    setTeaser(null);
                    setError(null);
                    setUnlockError(null);
                    setCancelState('idle');
                    try { localStorage.removeItem(TEASER_STORAGE_KEY); } catch (_) { /* ignore */ }
                  }}
                  className="text-sm underline hover:no-underline"
                  style={{ color: 'hsl(var(--gb-text-muted))' }}
                  data-testid="new-run-button"
                >
                  Run a new analysis
                </button>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <footer
          className="mt-16 pt-6 border-t"
          style={{ borderColor: 'hsl(var(--gb-gold))' }}
        >
          <FooterLinks />
        </footer>

        {/* Free-report email-entry dialog. Lives at the App root so it
            renders above all other UI when open and isn't unmounted by
            sample/loading state transitions. */}
        <FreeReportDialog
          open={freeReportDialogOpen}
          onOpenChange={setFreeReportDialogOpen}
          onUpgradeClick={() => {
            // Scroll the user back to the input form so they can run a paid analysis
            try {
              document.querySelector('[data-testid="input-form"]')?.scrollIntoView({ behavior: 'smooth' });
            } catch (_) { /* ignore */ }
          }}
        />
      </div>
    </div>
  );
}

export default App;
